import { OfflineSigner } from '@cosmjs/proto-signing';
import { ChainInfo } from '@keplr-wallet/types';

import { DeFiConnectProvider } from '@deficonnect/provider';
import { Eth } from 'web3-eth';

export enum Network {
  Cronos = 'CRONOS',
  CryptoOrg = 'CRYPTO_ORG',
  Ethereum = 'ETHEREUM',
  Cosmos = 'COSMOSHUB',
  Terra = 'TERRA',
  Juno = 'JUNO',
  Akash = 'AKASH',
  Canto = 'CANTO',
  Iris = 'IRIS',
  Celestia = 'CELESTIA',
}

export enum SourceChain { // solely to match API response
  cronos = 'Cronos',
  cryptoOrg = 'Crypto.org-Chain',
  ethereum = 'Ethereum',
  cosmos = 'Cosmos',
  terra = 'Terra',
  juno = 'Juno',
  akash = 'Akash',
  canto = 'Canto',
  iris = 'Iris',
  celestia = 'Celestia',
}

export enum WalletType {
  deficonnect = 'Crypto.com Onchain',
  metamask = 'Metamask',
  keplr = 'Keplr',
}

export enum CroChain {
  mainnet = 'crypto-org-chain-mainnet-1',
  testnet3 = 'testnet-croeseid-3',
  testnet4 = 'testnet-croeseid-4',
}

export enum CosmosHubChain {
  mainnet = 'cosmoshub-4',
  testnet = 'cosmoshub-4',
}

export enum TerraChain {
  mainnet = 'columbus-5',
  testnet = 'columbus-5',
}

export enum JunoChain {
  mainnet = 'juno-1',
  testnet = 'juno-1',
}

export enum AkashChain {
  mainnet = 'akashnet-2',
  testnet = 'akashnet-2',
}

export enum CantoChain {
  mainnet = 'canto_7700-1',
  testnet = 'canto_7700-1',
}

export enum IrisChain {
  mainnet = 'irishub-1',
  testnet = 'irishub-1',
}

export enum CelestiaChain {
  mainnet = 'celestia',
  testnet = 'celestia',
}

export type CosmosChain =
  | CroChain
  | CosmosHubChain
  | TerraChain
  | JunoChain
  | AkashChain
  | CantoChain
  | IrisChain
  | CelestiaChain;

export enum CronosChain {
  mainnet = 25,
  testnet = 338,
  gravityTestnet = 340,
}

export enum TxStatus {
  pending = 'pending',
  fulfilled = 'fulfilled',
  failed = 'failed',
}

export enum TxStep {
  deposit = 'deposit',
  transfer = 'transfer',
}

export type TxChain = EthChainId | CosmosChain | CronosChain;

export interface Tx {
  status: TxStatus;
  network: Network;
  chain: TxChain | null;
  asset: TokenAsset | null;
  amount: string;
  hash: string | null;
}

export interface BridgeTx {
  id: string;
  depositTx: Tx;
  transferTx: Tx;
}

export interface BridgeTxNotification {
  isHidden: boolean;
  bridgeTx: BridgeTx;
}

export interface BridgeTxApiResponse {
  pagination: BridgeTxApiPagination;
  result: BridgeTxApiSingleResult[] | [];
}

export interface BridgeTxApiPagination {
  total_record: number;
  total_page: number;
  current_page: number;
  limit: number;
}

export enum BridgeTxApiSingleResultStatus {
  pending = 'Pending',
  failed = 'FailedOnChain',
  cancelled = 'Cancelled',
  counterpartyConfirmed = 'CounterpartyConfirmed',
  counterpartyRejected = 'CounterpartyRejected',
  beta = 'Beta',
}

export interface BridgeTxApiSingleResult {
  uuid: string;
  bridgeType: string;
  sourceBlockHeight: string;
  sourceBlockTime: string;
  sourceTransactionId: string;
  sourceChain: string;
  sourceAddress: string;
  destinationChain: string;
  destinationAddress: string;
  amount: string;
  status: BridgeTxApiSingleResultStatus;
  denom: string;
  displayAmount: string;
  displayDenom: string;
  bridgeFeeAmount?: string;
  bridgeFeeDenom?: string;
  destinationBlockHeight?: string;
  destinationBlockTime?: string;
  destinationTransactionId?: string;
}

export type BridgeTxTableNetwork = {
  address: string;
  network: string;
};
export type BridgeTxTableTransactionHash = {
  source: string;
  sourceExplorer: string;
  destination: string;
  destinationExplorer: string;
};
export type BridgeTxTableAmountAndDate = {
  amount: string;
  dateTime: string;
};

export interface BridgeTxTableRow {
  isTestnet?: boolean;
  status: string;
  from: BridgeTxTableNetwork;
  to: BridgeTxTableNetwork;
  amountAndDateTime: BridgeTxTableAmountAndDate;
  transactionHash: BridgeTxTableTransactionHash;
}

export type BridgeTxAPIDataStatusMapping = {
  [key in string]: string;
};

export enum EthChainId {
  Mainnet = 1,
  Ropsten = 3,
  Rinkeby = 4,
  Goerli = 5,
  Kovan = 42,
  Cronos = 25,
  CronosTestnet = 338,
  CronosGravityTestnet = 340,
}

export type TokenAssetAddress = {
  [key in EthChainId | CosmosChain]: string;
};

export type TokenAssetNetworkPair = `${Network}:${Network}`;

export interface TokenAsset {
  address: TokenAssetAddress;
  decimals: number;
  logoURI: string;
  name: string;
  supportedNetworkPairs: TokenAssetNetworkPair[];
  symbol: string;
}

export interface Wallet {
  address: string;
  network: Network;
  chain: CosmosChain | EthChainId;
  defiProvider?: DeFiConnectProvider;
  eth?: Eth;
  signer?: OfflineSigner;
  type: WalletType;
  active: boolean;
}

export enum CronosEthereumTransferType {
  CronosPool = 'CronosPool',
  CronosWarp = 'CronosWarp',
}

export type BridgeTxApiSingleReponse = {
  result: BridgeTxApiSingleResult;
};

export interface crc21Contract {
  contract: string;
  auto_contract: string;
}

export type Explorers = {
  [key: string]: string;
};

export interface NetworkChainExplorer {
  [key: string | number]: Explorers;
}

export type Networks = {
  [key in Network]?: {
    disabled: boolean;
    hide?: boolean;
    toNetworks: {
      [key in Network]?: {
        disabled: boolean;
        hide?: boolean;
        addressInput: boolean;
      };
    };
  };
};

export interface CosmosChainInfo extends ChainInfo {
  bridgeRpcs?: string[];
}

export enum ExternalBridgeType {
  XyFinance = 'XyFinance',
  Symbiosis = 'Symbiosis',
  Unknown = 'Unknown', // for testing
}
